/** Table row, cell, and header style utilities. */
import { cn } from "@/utils/cn";
import type { TableVariant, TableCellAlign } from "./Table.types";

/** Variant → bg class. */
const variantClasses: Record<TableVariant, string> = {
  standard: "bg-white",
  quiet: "bg-background-gray",
};

/** Row: variant bg, header has transparent bg. */
export function getTableRowClasses(
  variant: TableVariant,
  /** True for thead rows. */
  inHeader: boolean,
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "transition-colors",
    !inHeader && "border-b border-light-lila/40",
    inHeader
      ? "bg-transparent"
      : cn("hover:bg-background-gray/50 data-[state=selected]:bg-purple/10", variantClasses[variant]),
    className
  );
}

/** Align → text-align class. */
const alignClasses: Record<TableCellAlign, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

/** Cell: padding, align, tabular-nums when numeric. */
export function getTableCellClasses(
  align: TableCellAlign,
  /** Right-align + tabular-nums. */
  numeric: boolean,
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "px-6 py-3 align-middle font-body text-sm text-gray whitespace-nowrap",
    alignClasses[align],
    numeric && "tabular-nums",
    "[&:has([role=checkbox])]:pr-0",
    className
  );
}

/** Header cell: height, uppercase, align. */
export function getTableHeadClasses(
  align: TableCellAlign,
  /** Optional merge. */
  className?: string
): string {
  return cn(
    "h-12 px-6 py-3 align-middle font-header text-xs font-semibold uppercase tracking-wider text-dark-invirtus",
    alignClasses[align],
    "[&:has([role=checkbox])]:pr-0",
    className
  );
}
